/**
 * Auth API Client
 * @apal/web-humanda
 *
 * API client for backend authentication endpoints.
 * Implements the AuthApi interface from @apal/shared-auth.
 */

import { createPortalFetch, extractErrorMessage } from "@apal/shared-utils";

import type {
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UserProfile,
} from "./types";
import { portalStorage } from "./storage";

// ==========================================================================
// Configuration
// ==========================================================================

/**
 * Get the API base URL from environment variables.
 * FAIL-FAST: Throws error if env vars are not configured.
 * No hardcoded fallback - ports come from .env.local (see port registry).
 */
function getApiBaseUrl(): string {
  // Vite's import.meta.env is loaded from .env files at build time
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (import.meta.env.VITE_API_URL) {
    // Strip /api/v1 suffix if present (VITE_API_URL includes path)
    return import.meta.env.VITE_API_URL.replace(/\/api\/v1$/, "");
  }
  throw new Error(
    "VITE_API_BASE_URL or VITE_API_URL must be set in .env.local. " +
    "Check port registry at ~/apps/local-dev-port-offsets for correct ports."
  );
}

const AUTH_BASE_PATH = "/api/v1/auth";
const API_BASE_URL = getApiBaseUrl();
const portalFetch = createPortalFetch('HUMANDA');

// ==========================================================================
// Error Class
// ==========================================================================

export class AuthApiError extends Error {
  public readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "AuthApiError";
    this.statusCode = statusCode;
  }
}

// ==========================================================================
// Helper Functions
// ==========================================================================

function transformAuthResponse(data: Record<string, unknown>): AuthResponse {
  const user = data.user as Record<string, unknown>;
  const tokens = data.tokens as Record<string, unknown>;
  const profile = data.profile as Record<string, unknown>;

  return {
    user: {
      id: user.id as string,
      email: user.email as string,
      firstName: (user.first_name as string) ?? null,
      lastName: (user.last_name as string) ?? null,
    },
    tokens: {
      accessToken: tokens.access_token as string,
      refreshToken: tokens.refresh_token as string,
      expiresAt: tokens.expires_at as number,
    },
    profile: {
      companyId: (profile.company_id as string) ?? null,
      companyName: (profile.company_name as string) ?? null,
      userType: profile.user_type as AuthResponse["profile"]["userType"],
      tierLevel: (profile.tier_level as number) ?? 1,
      canAccessApal: profile.can_access_apal as boolean,
      canAccessHumanda: profile.can_access_humanda as boolean,
    },
  };
}

function transformUserProfile(data: Record<string, unknown>): UserProfile {
  return {
    id: data.id as string,
    email: data.email as string,
    firstName: (data.first_name as string) ?? null,
    lastName: (data.last_name as string) ?? null,
    displayName: (data.display_name as string) ?? null,
    avatarUrl: (data.avatar_url as string) ?? null,
    companyId: (data.company_id as string) ?? null,
    companyName: (data.company_name as string) ?? null,
    departmentId: (data.department_id as string) ?? null,
    userType: data.user_type as UserProfile["userType"],
    tierLevel: data.tier_level as number,
    canAccessApal: data.can_access_apal as boolean,
    canAccessHumanda: data.can_access_humanda as boolean,
    createdAt: data.created_at as string,
  };
}

function getAuthHeaders(): Record<string, string> {
  const token = portalStorage.getAccessToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function storeTokens(accessToken: string, refreshToken: string): void {
  portalStorage.storeTokens(accessToken, refreshToken);
}

function clearTokens(): void {
  portalStorage.clearTokens();
}

async function handleErrorResponse(response: Response): Promise<never> {
  let message = "An error occurred";
  let data: Record<string, unknown> = {};

  try {
    data = await response.json();
    message = extractErrorMessage(data.detail, message);
  } catch {
    // Failed to parse JSON
  }

  if (response.status === 401) {
    if (message.includes("refresh")) {
      throw new AuthApiError("Session expired. Please log in again.", response.status);
    }
    throw new AuthApiError("Invalid email or password", response.status);
  }

  throw new AuthApiError(message, response.status);
}

// ==========================================================================
// Auth API Client
// ==========================================================================

export const authApi = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        ...(data.userType && { user_type: data.userType }),
        ...(data.companyName && { company_name: data.companyName }),
        ...(data.inviteCode && { invite_code: data.inviteCode }),
      }),
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const responseData = await response.json();
    const transformed = transformAuthResponse(responseData);
    storeTokens(transformed.tokens.accessToken, transformed.tokens.refreshToken);
    return transformed;
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        ...(data.rememberMe !== undefined && { remember_me: data.rememberMe }),
      }),
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const responseData = await response.json();
    const transformed = transformAuthResponse(responseData);
    storeTokens(transformed.tokens.accessToken, transformed.tokens.refreshToken);
    return transformed;
  },

  async logout(): Promise<void> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
    });

    clearTokens();

    if (!response.ok && response.status !== 401) {
      await handleErrorResponse(response);
    }
  },

  async refreshToken(): Promise<void> {
    const refreshToken = portalStorage.getRefreshToken();

    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      await handleErrorResponse(response);
    }

    const responseData = await response.json();
    storeTokens(
      responseData.access_token as string,
      responseData.refresh_token as string
    );
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: data.email }),
    });

    if (!response.ok && response.status !== 204) {
      await handleErrorResponse(response);
    }
  },

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: data.token, new_password: data.newPassword }),
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }
  },

  async getProfile(): Promise<UserProfile> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/me`, {
      method: "GET",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const responseData = await response.json();
    return transformUserProfile(responseData);
  },

  /**
   * Bootstrap-only `/auth/me` that routes through the NON-navigating fetch path
   * (`navigateOn401: false`). On a 401 it throws an `AuthApiError` (status 401)
   * WITHOUT triggering the shared `createPortalFetch` window.location navigation,
   * so the auth-bootstrap state machine can handle the 401 locally (refresh +
   * retry, else clear + login) with no reload loop.
   * REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX (PC-011).
   */
  async getProfileNoRedirect(): Promise<UserProfile> {
    const response = await portalFetch(`${API_BASE_URL}${AUTH_BASE_PATH}/me`, {
      method: "GET",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
      navigateOn401: false,
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const responseData = await response.json();
    return transformUserProfile(responseData);
  },

  getAccessToken(): string | null {
    return portalStorage.getAccessToken();
  },

  isAuthenticated(): boolean {
    return portalStorage.isAuthenticated();
  },

  /**
   * Synchronous pre-check for refresh-token presence (AC-2 → zero refresh calls
   * when no refresh token is stored).
   */
  hasRefreshToken(): boolean {
    const token = portalStorage.getRefreshToken();
    return token !== null && token !== "";
  },

  /**
   * Clear stored tokens (used by the bootstrap state machine on a terminal 401).
   */
  clearTokens(): void {
    portalStorage.clearTokens();
  },
};

/**
 * Handle API errors and return user-friendly messages
 */
export function handleAuthError(error: unknown): string {
  if (error instanceof AuthApiError) {
    if (error.statusCode === 429) {
      return "Too many attempts. Please try again later.";
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}
